import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useField } from "formik";
import { AgGrid, ErrorMessage, Flex, Grid, Label, Text, TextInput } from "@jsq/ds/core";
import { useFormState } from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import {
  filterTreeRowsWithAncestors,
  toTreeRowId,
  type HierarchyNodeData,
  type TypeHierarchy,
} from "../../DataStructure/DataPackageSlideout/HierarchyTreeGrid";
import * as styles from "./styles_vanilla.css";
import { supportsTransactionTypes } from "./supportedTemplateTypes";

const HELP_TEXT =
  "Select the transaction types to include when generating reports with this template";

export type TransactionTypeRow = {
  id: string;
  path: string[];
  label: string;
  code: string;
  nodeId: number | null;
  isLeaf: boolean;
  isAncestorOnly?: boolean;
};

const isLeafNode = (node: HierarchyNodeData): boolean =>
  node.is_leaf === true || !node.children || node.children.length === 0;

/** Flatten the type-hierarchy tree into selectable rows. The synthetic root
 *  is dropped so top-level groups render as siblings — mirroring how
 *  `AddFieldPickerSlideout` walks the same `finpubDataPackage.typeHierarchy`
 *  blob. Every node carries its `typeHierarchyNodeId` (`node.id`) so a
 *  selection maps directly onto `finpubQueryCreate/Update`'s
 *  `transactionTypeNodeIds`, plus an `isLeaf` flag so we can persist only
 *  leaf ids even when a parent group is selected.
 *
 *  Paths are built from `code`, not `name`: names are display strings that may
 *  repeat between siblings, while codes are the hierarchy's identifiers (this
 *  also matches how `HierarchyTreeGrid` addresses the same blob). */
const flattenHierarchy = (
  node: HierarchyNodeData,
  parentPath: string[] = [],
  isRoot = true,
): TransactionTypeRow[] => {
  const path = isRoot ? parentPath : [...parentPath, node.code];
  const row: TransactionTypeRow | null = isRoot
    ? null
    : {
        id: toTreeRowId(path),
        path,
        label: node.name,
        code: node.code,
        nodeId: node.id ?? null,
        isLeaf: isLeafNode(node),
      };
  const childRows =
    node.children?.flatMap((child) => flattenHierarchy(child, path, false)) ?? [];
  return row ? [row, ...childRows] : childRows;
};

export const flattenTransactionTypeHierarchy = (
  typeHierarchy: TypeHierarchy | null,
): TransactionTypeRow[] => {
  const root = typeHierarchy?.tree_structure?.root;
  return root ? flattenHierarchy(root) : [];
};

const matchesTransactionType = (row: TransactionTypeRow, needle: string): boolean =>
  row.label.toLowerCase().includes(needle) || row.code.toLowerCase().includes(needle);

export const getTransactionTypeRows = (
  typeHierarchy: TypeHierarchy | null,
  searchTerm: string,
): TransactionTypeRow[] =>
  filterTreeRowsWithAncestors(
    flattenTransactionTypeHierarchy(typeHierarchy),
    searchTerm,
    matchesTransactionType,
  );

/** Read-only view rows: just the selected leaves plus their ancestor groups
 *  for context. Reuses the ancestor-preserving filter with a selection
 *  predicate instead of a text search (the term is only used to short-circuit
 *  an empty query, so any non-empty sentinel works). */
export const getSelectedTransactionTypeRows = (
  typeHierarchy: TypeHierarchy | null,
  selectedIds: readonly number[],
): TransactionTypeRow[] => {
  const selectedSet = new Set(selectedIds);
  return filterTreeRowsWithAncestors(
    flattenTransactionTypeHierarchy(typeHierarchy),
    "selected",
    (row) => row.nodeId !== null && selectedSet.has(row.nodeId),
  );
};

type TransactionTypesFieldProps = {
  name?: string;
  disabled?: boolean;
  typeHierarchy?: TypeHierarchy | null;
  "data-testid"?: string;
};

type TransactionTypesBodyProps = {
  name: string;
  typeHierarchy: TypeHierarchy | null;
};

type GridContext = {
  searchValue: string;
  name?: string;
};

type LabelRendererParams = AgGrid.ICellRendererParams<TransactionTypeRow, string, GridContext>;

const DEFAULT_COL_DEF: AgGrid.ColDef<TransactionTypeRow> = {
  flex: 1,
  minWidth: 100,
};

const getRowId = (params: AgGrid.GetRowIdParams<TransactionTypeRow>) => params.data?.id ?? "";

const getDataPath = (data: TransactionTypeRow) => data.path;

const isDescendantOf = (row: TransactionTypeRow, parentPath: readonly string[]): boolean =>
  row.path.length > parentPath.length &&
  parentPath.every((segment, index) => row.path[index] === segment);

const resolveLeafDescendants = (
  parentPath: readonly string[],
  allRows: readonly TransactionTypeRow[],
): number[] =>
  allRows
    .filter(
      (row): row is TransactionTypeRow & { nodeId: number } =>
        row.isLeaf &&
        row.nodeId !== null &&
        row.nodeId !== undefined &&
        isDescendantOf(row, parentPath),
    )
    .map((row) => row.nodeId);

/** Selecting a parent group auto-selects its descendants, but we only persist
 *  leaf ids — a group is fully represented by the leaves beneath it. When
 *  search filtering hides a parent's children, we resolve the parent's leaf
 *  descendants from the full (unfiltered) tree so Formik always has leaf ids. */
const getSelectedNodeIds = (
  api: AgGrid.GridApi<TransactionTypeRow>,
  allRows: readonly TransactionTypeRow[],
): number[] => {
  const ids = new Set<number>();
  for (const row of api.getSelectedRows() ?? []) {
    if (row.nodeId === null || row.nodeId === undefined) continue;
    if (row.isLeaf) {
      ids.add(row.nodeId);
    } else {
      for (const leafId of resolveLeafDescendants(row.path, allRows)) {
        ids.add(leafId);
      }
    }
  }
  return [...ids];
};

const USER_SELECTION_SOURCES: ReadonlySet<string> = new Set([
  "checkboxSelected",
  "rowClicked",
  "spaceKey",
  "uiSelectAll",
  "uiSelectAllFiltered",
  "uiSelectAllCurrentPage",
]);

const SelectedCountLabel = ({
  count,
  className = styles.selectedCountPanel,
}: {
  count: number;
  className?: string;
}) => (
  <Flex alignItems="center" gap={SPACING.X_SMALL} className={className}>
    <Text variant="secondary-md" asSpan>
      Selected:
    </Text>
    <Text
      variant="regular-md"
      asSpan
      className={styles.selectedCountValue}
      data-testid="transaction-type-selected-count"
    >
      {count}
    </Text>
  </Flex>
);

const TransactionTypeSelectedCountPanel = (
  params: AgGrid.IStatusPanelParams<TransactionTypeRow, GridContext>,
) => {
  const [{ value = [] }] = useField<number[]>(
    params.context?.name ?? "supportedTransactionTypes",
  );
  return (
    <SelectedCountLabel count={value.length} className={styles.selectedCountStatusPanel} />
  );
};

const TransactionTypeLabelRenderer = (params: LabelRendererParams) => {
  const searchValue = params.context?.searchValue ?? "";
  const label = params.data?.label ?? "";
  const isAncestorOnly = params.data?.isAncestorOnly === true;

  return (
    <Text
      variant="regular-md"
      className={isAncestorOnly ? styles.ancestorOnlyText : undefined}
      asSpan
    >
      <HighlightText text={label} searchValue={isAncestorOnly ? "" : searchValue} />
    </Text>
  );
};

const FieldHeader = ({ showHelpText }: { showHelpText: boolean }) => (
  <Flex flexDirection="column" className={styles.fieldIntro}>
    <Label>Supported transaction types</Label>
    {showHelpText && (
      <Text
        variant="secondary-sm"
        className={styles.helpText}
        data-cy="input-wrapper-help-text"
      >
        {HELP_TEXT}
      </Text>
    )}
  </Flex>
);

/** Non-interactive view of the persisted selection — only the selected leaves
 *  (and their parent groups, muted for context) are shown. */
const ReadOnlyTransactionTypes = ({ name, typeHierarchy }: TransactionTypesBodyProps) => {
  const [{ value: selected = [] }] = useField<number[]>(name);

  const rowData = useMemo(
    () => getSelectedTransactionTypeRows(typeHierarchy, selected),
    [typeHierarchy, selected],
  );

  const context = useMemo<GridContext>(() => ({ searchValue: "" }), []);

  const autoGroupColumnDef = useMemo<AgGrid.ColDef<TransactionTypeRow>>(
    () => ({
      field: "label",
      minWidth: 200,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: TransactionTypeLabelRenderer,
      },
    }),
    [],
  );

  const { gridOptions } = useAgGridDefaults({
    overrides: {
      domLayout: "autoHeight",
      rowHeight: 50,
      headerHeight: 0,
      suppressContextMenu: true,
      suppressCellFocus: true,
      suppressHorizontalScroll: true,
      pagination: false,
      sideBar: false,
      statusBar: undefined,
    },
  });

  return (
    <Flex flexDirection="column" gap={SPACING.SMALL}>
      <SelectedCountLabel count={selected.length} />
      {rowData.length === 0 ? (
        <Text
          variant="secondary-sm"
          color={Colors.Neutral600}
          data-testid="transaction-types-readonly-empty"
        >
          No transaction types selected
        </Text>
      ) : (
        <Flex
          flexDirection="column"
          className={styles.readOnlyPanel}
          data-testid="transaction-types-readonly"
        >
          <Flex flexDirection="column" className={styles.readOnlyGrid}>
            <Grid<TransactionTypeRow>
              rowData={[...rowData]}
              getRowId={getRowId}
              columnDefs={[]}
              defaultColDef={DEFAULT_COL_DEF}
              treeData
              getDataPath={getDataPath}
              autoGroupColumnDef={autoGroupColumnDef}
              groupDefaultExpanded={-1}
              gridOptions={gridOptions}
              suppressGroupRowsSticky
              context={context}
              reactiveCustomComponents
              data-testid="transaction-types-readonly-grid"
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const EditableTransactionTypes = ({ name, typeHierarchy }: TransactionTypesBodyProps) => {
  const [{ value: selected = [] }, meta, { setValue }] = useField<number[]>(name);
  const { submitCount } = useFormState();
  const [searchValue, setSearchValue] = useState("");
  const gridApiRef = useRef<AgGrid.GridApi<TransactionTypeRow> | null>(null);

  const allRows = useMemo(
    () => flattenTransactionTypeHierarchy(typeHierarchy),
    [typeHierarchy],
  );

  const rowData = useMemo(
    () => filterTreeRowsWithAncestors(allRows, searchValue, matchesTransactionType),
    [allRows, searchValue],
  );

  // Show after submit when Formik has an error, or when nothing is selected
  // (covers array-shaped Yup errors where meta.error isn't a plain string).
  const hasError = submitCount > 0 && (Boolean(meta.error) || selected.length === 0);

  const updateSelection = useCallback(
    (next: number[]) => {
      void setValue(next);
    },
    [setValue],
  );

  const syncFormSelectionToGrid = useCallback(
    (api: AgGrid.GridApi<TransactionTypeRow>) => {
      const selectedSet = new Set(selected);

      api.forEachNode((node) => {
        const row = node.data;
        if (!row) return;

        if (row.isLeaf) {
          if (row.nodeId === null || row.nodeId === undefined) return;
          const shouldSelect = selectedSet.has(row.nodeId);
          if (node.isSelected() !== shouldSelect) node.setSelected(shouldSelect);
          return;
        }

        // Parent whose leaf children are all filtered out by search — AG
        // Grid's groupSelectsChildren can't derive state, so we check
        // whether every leaf descendant in the full tree is selected.
        const hasVisibleLeafChild = node.allLeafChildren?.some((child) => child.data?.isLeaf);
        if (hasVisibleLeafChild) return;

        const leafDescendants = resolveLeafDescendants(row.path, allRows);
        if (leafDescendants.length === 0) return;

        const shouldSelect = leafDescendants.every((id) => selectedSet.has(id));
        if (node.isSelected() !== shouldSelect) {
          node.setSelected(shouldSelect);
        }
      });
    },
    [selected, allRows],
  );

  const onGridReady = useCallback(
    (event: AgGrid.GridReadyEvent<TransactionTypeRow>) => {
      gridApiRef.current = event.api;
      syncFormSelectionToGrid(event.api);
    },
    [syncFormSelectionToGrid],
  );

  useEffect(() => {
    if (!gridApiRef.current) return;
    syncFormSelectionToGrid(gridApiRef.current);
  }, [syncFormSelectionToGrid, rowData]);

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;

    if (searchValue.trim()) {
      api.expandAll();
    } else {
      api.forEachNode((node) => {
        node.setExpanded(node.level === 0);
      });
    }

    api.refreshCells({ force: true });
  }, [searchValue, rowData]);

  const onSelectionChanged = useCallback(
    (event: AgGrid.SelectionChangedEvent<TransactionTypeRow>) => {
      if (!USER_SELECTION_SOURCES.has(event.source)) return;

      const selectedInGrid = getSelectedNodeIds(event.api, allRows);
      if (!searchValue.trim()) {
        updateSelection(selectedInGrid);
        return;
      }

      // While searching, the grid only holds the filtered rows, so keep the
      // saved selections outside the current filter and merge in the in-view ones.
      const visibleLeafIds = new Set<number>();
      for (const row of rowData) {
        if (row.isLeaf && row.nodeId !== null) visibleLeafIds.add(row.nodeId);
      }
      const preserved = selected.filter((id) => !visibleLeafIds.has(id));
      const inView = selectedInGrid.filter((id) => visibleLeafIds.has(id));
      updateSelection([...new Set([...preserved, ...inView])]);
    },
    [updateSelection, allRows, searchValue, rowData, selected],
  );

  const isRowSelectable = useCallback(
    (node: AgGrid.IRowNode<TransactionTypeRow>) => Boolean(node.data),
    [],
  );

  const context = useMemo<GridContext>(() => ({ searchValue, name }), [searchValue, name]);

  const gridOptions = useMemo<AgGrid.GridOptions<TransactionTypeRow>>(
    () => ({
      headerHeight: 0,
      statusBar: {
        statusPanels: [
          {
            statusPanel: TransactionTypeSelectedCountPanel,
            key: "transactionTypeSelectedCount",
            align: "left",
          },
        ],
      },
    }),
    [],
  );

  const autoGroupColumnDef = useMemo<AgGrid.ColDef<TransactionTypeRow>>(
    () => ({
      field: "label",
      minWidth: 200,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: TransactionTypeLabelRenderer,
      },
    }),
    [],
  );

  const groupDefaultExpanded = useMemo(() => (searchValue.trim() ? -1 : 1), [searchValue]);

  return (
    <>
      <TextInput
        className={styles.searchField}
        value={searchValue}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setSearchValue(event.target.value)
        }
        icon="search"
        isClearable
        isFullWidth
        onClearValue={() => setSearchValue("")}
        placeholder="Search transaction types"
        data-testid="create-query-transaction-types-search"
      />

      <Flex
        flexDirection="column"
        className={styles.pickerPanel[hasError ? "error" : "default"]}
        data-testid="create-query-transaction-types-picker"
        data-invalid={hasError || undefined}
      >
        <Flex flexDirection="column" className={styles.transactionTypeGrid}>
          <Grid<TransactionTypeRow>
            rowData={[...rowData]}
            getRowId={getRowId}
            columnDefs={[]}
            defaultColDef={DEFAULT_COL_DEF}
            treeData
            getDataPath={getDataPath}
            autoGroupColumnDef={autoGroupColumnDef}
            groupDefaultExpanded={groupDefaultExpanded}
            isRowSelectable={isRowSelectable}
            rowSelection="multiple"
            suppressRowClickSelection
            groupSelectsChildren
            groupSelectsFiltered={Boolean(searchValue.trim())}
            suppressGroupRowsSticky
            gridOptions={gridOptions}
            onSelectionChanged={onSelectionChanged}
            onGridReady={onGridReady}
            context={context}
            overlayNoRowsTemplate="No transaction types match your search."
            reactiveCustomComponents
            data-testid="transaction-types-grid"
          />
        </Flex>
      </Flex>

      {hasError && (
        <ErrorMessage>
          {typeof meta.error === "string" ? meta.error : "Select at least one transaction type"}
        </ErrorMessage>
      )}
    </>
  );
};

export const TransactionTypesField = ({
  name = "supportedTransactionTypes",
  disabled = false,
  typeHierarchy = null,
  "data-testid": dataTestId = "create-query-supported-transaction-types",
}: TransactionTypesFieldProps) => {
  const { values } = useFormState<{ supportedTemplateType?: string }>();
  if (!supportsTransactionTypes(values.supportedTemplateType)) return null;

  return (
    <Flex
      flexDirection="column"
      className={styles.field}
      data-field={name}
      data-testid={dataTestId}
    >
      <FieldHeader showHelpText={!disabled} />
      {disabled ? (
        <ReadOnlyTransactionTypes name={name} typeHierarchy={typeHierarchy} />
      ) : (
        <EditableTransactionTypes name={name} typeHierarchy={typeHierarchy} />
      )}
    </Flex>
  );
};
